/**
 * Pure tier-vs-tier diff used by the upgrade-plan cards. Given a higher
 * tier and its immediate-lower tier from the pricing catalog, returns
 * exactly what the higher tier *adds*: new features, and limits whose
 * value strictly increased.
 *
 * Kept side-effect free (no React, no fetch) so it can move into a unit
 * test or a pricing-page export without changes. The `?` arguments make
 * partial catalog payloads (e.g. an upgrade tier with no lower-tier
 * equivalent or vice-versa) safe — callers get back empty arrays rather
 * than crashes.
 */

import type { FeatureLabel, TierCatalog } from './hooks';

export interface LimitDelta {
  /** Raw key into `TierCatalog.limits` — used by callers as the React key. */
  key: string;
  /** Display name from `LIMIT_LABELS[key]`. Falls back to the raw key. */
  name: string;
  /** Suffix string ("/month", "GB", etc.). Empty when unit is implied. */
  unit: string;
  /** Lower tier's value for this limit (`-1` = unlimited, `0` = not on plan). */
  fromValue: number;
  /** Higher tier's value for this limit. */
  toValue: number;
  /**
   * `'unlock'` when the lower tier was 0 (feature gated behind a numeric
   * cap of zero) and the higher tier flips it positive — frame as "Now
   * available". `'increase'` for genuine numeric jumps including
   * `n → unlimited`.
   */
  kind: 'unlock' | 'increase';
}

export interface PlanDiff {
  newFeatures: FeatureLabel[];
  limitUpgrades: LimitDelta[];
}

/**
 * Compute the "what's new" delta between an upgrade tier and its
 * immediate-lower tier. Both arguments come from the public pricing
 * catalog (`/subscriptions/pricing`).
 *
 * Rules — see PlanCard.tsx PM brief for the full edge-case list:
 *   1. Features: set difference on `featureLabels.id`. Order preserved
 *      from the higher tier's array (already grouped by category in the
 *      shared FEATURES literal).
 *   2. Limits: compare each key in the higher tier's `limits`. We
 *      consider any of these an "upgrade":
 *        - `lower > 0` && `upper > lower` (genuine increase)
 *        - `lower >= 0` && `upper === -1` (unlocked unlimited)
 *        - `lower === 0` && `upper > 0` (numeric unlock, `kind: 'unlock'`)
 *      Equal values, `lower === -1 && upper === -1`, and downgrades
 *      (`upper < lower`) are skipped.
 *   3. Caller controls display copy through the `limitLabels` lookup so
 *      this function stays platform-agnostic.
 *
 * @param current   The higher tier (the one the upgrade card represents).
 * @param lower     The immediate-lower tier (for Growth → Free; for
 *                  Professional → Growth). May be `null` if there is no
 *                  lower tier configured for this currency, in which
 *                  case the full feature + limit list is returned.
 * @param limitLabels Lookup from limit key → `{ name, unit }`. Pass the
 *                  shared `LIMIT_LABELS` from `@centraoneplus/shared`.
 * @param onAnomaly Optional callback fired when we encounter a "limit
 *                  downgrade" (upper < lower for the same key) — should
 *                  not happen in a real catalog. Defaults to a noop.
 */
export function diffPlan(
  current: TierCatalog | null | undefined,
  lower: TierCatalog | null | undefined,
  limitLabels: Record<string, { name: string; unit: string }>,
  onAnomaly?: (msg: string) => void,
): PlanDiff {
  if (!current) return { newFeatures: [], limitUpgrades: [] };

  // No lower tier — caller is rendering the lowest priced tier as if
  // it were a fresh upgrade card. Return everything we have to say,
  // skipping `0`-valued limits (a "you have 0 of X" row is not a sales
  // signal). Every present row is an unlock from the implicit "no
  // plan" baseline.
  if (!lower) {
    const limitUpgrades: LimitDelta[] = [];
    for (const [rawKey, value] of Object.entries(current.limits)) {
      if (value === 0) continue;
      const label = limitLabels[rawKey];
      limitUpgrades.push({
        key: rawKey,
        name: label?.name ?? rawKey,
        unit: label?.unit ?? '',
        fromValue: 0,
        toValue: value,
        kind: 'unlock',
      });
    }
    return { newFeatures: [...current.featureLabels], limitUpgrades };
  }

  const lowerFeatureIds = new Set(lower.featureLabels.map((f) => f.id));
  const newFeatures = current.featureLabels.filter(
    (f) => !lowerFeatureIds.has(f.id),
  );

  const limitUpgrades: LimitDelta[] = [];
  for (const [rawKey, upperValue] of Object.entries(current.limits)) {
    const lowerValue = lower.limits[rawKey];
    if (lowerValue === undefined) {
      // Limit key only on the upper tier (e.g. introduced in a later
      // catalog rev) — treat as a fresh unlock from 0. Skip `0` toValue
      // here for the same reason as the no-lower-tier branch above.
      if (upperValue === 0) continue;
      const label = limitLabels[rawKey];
      limitUpgrades.push({
        key: rawKey,
        name: label?.name ?? rawKey,
        unit: label?.unit ?? '',
        fromValue: 0,
        toValue: upperValue,
        kind: 'unlock',
      });
      continue;
    }

    if (lowerValue === upperValue) continue;
    if (lowerValue === -1 && upperValue === -1) continue;

    // Both finite and upper is strictly less → catalog anomaly. Skip
    // silently (do NOT advertise as an upgrade) and report once.
    if (lowerValue !== -1 && upperValue !== -1 && upperValue < lowerValue) {
      onAnomaly?.(
        `Plan-catalog anomaly: limit "${rawKey}" is lower on the upgrade tier ` +
          `(${upperValue}) than on its predecessor (${lowerValue}). Skipping.`,
      );
      continue;
    }

    // Upper unlimited from a finite (incl. zero) lower — always an upgrade.
    // Otherwise lower must be finite and upper strictly greater.
    const isUpgrade =
      upperValue === -1 || (lowerValue !== -1 && upperValue > lowerValue);
    if (!isUpgrade) continue;

    const label = limitLabels[rawKey];
    limitUpgrades.push({
      key: rawKey,
      name: label?.name ?? rawKey,
      unit: label?.unit ?? '',
      fromValue: lowerValue,
      toValue: upperValue,
      kind: lowerValue === 0 && upperValue !== 0 ? 'unlock' : 'increase',
    });
  }

  return { newFeatures, limitUpgrades };
}
